import React, { useState } from 'react';
import { useReplicate } from '../hooks/useReplicate';
import ImageDisplay from './ImageDisplay';
import { downloadImageFromUrl } from '../utils/imageUtils';
import '../styles/BaseThumbnailGenerator.css';

const BaseThumbnailGenerator = ({ thumbnailData, onBaseThumbnailGenerated }) => {
  const [customTitle, setCustomTitle] = useState('');
  const [additionalPrompt, setAdditionalPrompt] = useState('');
  const [generatedImage, setGeneratedImage] = useState(null);
  const { loading, error, progress, generateThumbnail, clearError } = useReplicate();

  const handleGenerate = async (e) => {
    e.preventDefault();
    
    if (!customTitle.trim()) {
      alert('Please enter a custom title for your thumbnail');
      return;
    }

    try {
      clearError();
      const result = await generateThumbnail(
        thumbnailData.thumbnailUrl,
        customTitle,
        additionalPrompt
      );
      
      // Result should be an array of URLs from replicate
      const imageUrl = Array.isArray(result) ? result[0] : result;
      
      const generatedData = {
        url: imageUrl,
        title: customTitle,
        prompt: additionalPrompt,
        originalThumbnail: thumbnailData,
      };
      
      setGeneratedImage(generatedData);
      onBaseThumbnailGenerated(generatedData);
      
    } catch (err) {
      console.error('Generation failed:', err);
    }
  };

  if (!thumbnailData) {
    return (
      <div className="base-thumbnail-generator component-card disabled">
        <h2>Step 2: Generate AI Base Thumbnail</h2>
        <p className="disabled-message">
          Please extract a YouTube thumbnail first to proceed with AI generation.
        </p>
      </div>
    );
  }

  return (
    <div className="base-thumbnail-generator component-card">
      <h2>Step 2: Generate AI Base Thumbnail</h2>
      
      <div className="generator-content">
        <div className="input-section">
          <div className="original-thumbnail">
            <h3>Original YouTube Thumbnail</h3>
            <ImageDisplay 
              src={thumbnailData.thumbnailUrl} 
              alt="Original YouTube thumbnail"
              className="original-img"
            />
          </div>
          
          <form onSubmit={handleGenerate} className="generation-form">
            <div className="form-group">
              <label htmlFor="customTitle">
                Custom Title Text *
              </label>
              <input
                id="customTitle"
                type="text"
                value={customTitle}
                onChange={(e) => setCustomTitle(e.target.value)}
                placeholder="Enter your podcast title"
                className="title-input"
                disabled={loading}
                maxLength={100}
              />
              <small className="input-help">
                This text will be added to your thumbnail as a stylish overlay
              </small>
            </div>
            
            <div className="form-group">
              <label htmlFor="additionalPrompt">
                Additional Design Instructions (Optional)
              </label>
              <textarea
                id="additionalPrompt"
                value={additionalPrompt}
                onChange={(e) => setAdditionalPrompt(e.target.value)}
                placeholder="e.g., make it more colorful, add a gradient background, modern style..."
                className="prompt-input"
                disabled={loading}
                rows={3}
                maxLength={500}
              />
              <small className="input-help">
                Describe any specific design changes you want
              </small>
            </div>
            
            <button 
              type="submit" 
              className="generate-btn"
              disabled={loading || !customTitle.trim()}
            >
              {loading ? 'Generating...' : 'Generate AI Thumbnail'}
            </button>
          </form>
          
          {progress && (
            <div className="progress-indicator">
              <div className="progress-text">{progress}</div>
              <div className="progress-bar">
                <div className="progress-fill"></div>
              </div>
            </div>
          )}
          
          {error && (
            <div className="error-message">
              <p>❌ {error}</p>
              <button onClick={clearError} className="clear-error-btn">
                Dismiss
              </button>
            </div>
          )}
        </div>
        
        {generatedImage && (
          <div className="generated-section">
            <h3>Generated Base Thumbnail</h3>
            <ImageDisplay 
              src={generatedImage.url} 
              alt="AI Generated thumbnail"
              className="generated-img"
            />
            <div className="generation-details">
              <p><strong>Title:</strong> {generatedImage.title}</p>
              {generatedImage.prompt && (
                <p><strong>Design Instructions:</strong> {generatedImage.prompt}</p>
              )}
            </div>
            <div className="download-actions">
              <button 
                onClick={() => downloadImageFromUrl(generatedImage.url, 'base-thumbnail.png')}
                className="download-link-btn"
                title="Download base thumbnail"
              >
                💾 Download Base Thumbnail
              </button>
            </div>
          </div>
        )}
      </div>
      
      <div className="help-text">
        <p>🤖 AI will remove people from the thumbnail and add your custom title</p>
        <p>The result will be used as the base layer for your final thumbnail</p>
      </div>
    </div>
  );
};

export default BaseThumbnailGenerator;
